import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Heading,
  Button,
  Card,
  CardBody,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  HStack,
  Badge,
  IconButton,
  VStack,
  Text,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  InputGroup,
  InputRightElement,
  Spinner,
  useBreakpointValue,
  Collapse,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { FiEdit, FiTrash2, FiPlus, FiRefreshCw, FiSearch, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import * as FiIcons from 'react-icons/fi';
import { menuApi, menuCategoryApi, menuSubCategoryApi } from '../config/api';

const toSecureUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://')) return `https://${url.slice(7)}`;
  if (url.startsWith('https://')) return url;
  return `${window.location.origin}${url.startsWith('/') ? url : `/${url}`}`;
};

const ManageMenu = () => {
  const [menus, setMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [deleteMenu, setDeleteMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedSubCategories, setExpandedSubCategories] = useState(new Set());
  const toast = useToast();
  
  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef();

  const [formData, setFormData] = useState({
    MENU_NAME: '',
    MENU_STATUS: 1,
    MENU_URL: '',
    MENUSUBCATEGORY_ID: '',
    MENU_DEV: '',
    selectedCategoryId: null,
  });

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const integratedToken = localStorage.getItem('integratedToken');
      if (!integratedToken) {
        setLoadingCategories(false);
        return;
      }

      const response = await menuCategoryApi.get('/list');
      if (response.data.status === 200) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchSubCategories = async () => {
    try {
      const integratedToken = localStorage.getItem('integratedToken');
      if (!integratedToken) return;

      const response = await menuSubCategoryApi.get('/list');
      if (response.data.status === 200) {
        setSubCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchMenus = async () => {
    setLoading(true);
    try {
      const integratedToken = localStorage.getItem('integratedToken');
      if (!integratedToken) {
        toast({
          title: 'Token tidak ditemukan',
          description: 'Silakan login ulang untuk mendapatkan token',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      const params = {};
      if (searchQuery) {
        params.search = searchQuery;
      }
      if (statusFilter !== '') {
        params.status = statusFilter;
      }

      const response = await menuApi.get('/list', { params });
      if (response.data.status === 200) {
        setMenus(response.data.data || []);
      } else {
        toast({
          title: 'Error',
          description: response.data.message || 'Gagal mengambil data menu',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching menus:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengambil data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
    fetchMenus();
  }, []);

  // Organize data into tree structure grouped by type (be/fe)
  const treeData = useMemo(() => {
    const treeByType = {
      be: {}, // Back End
      fe: {}, // Front End
      other: {} // No type specified
    };
    
    // First, add all active categories grouped by type
    categories
      .filter(cat => cat.MENUCATEGORY_STATUS === 1)
      .forEach(category => {
        const type = category.MENUCATEGORY_TYPE || 'other';
        const typeKey = type === 'be' ? 'be' : type === 'fe' ? 'fe' : 'other';
        
        if (!treeByType[typeKey]) {
          treeByType[typeKey] = {};
        }
        
        treeByType[typeKey][category.MENUCATEGORY_ID] = {
          category,
          subCategories: {}
        };
      });
    
    // Then, add all active subcategories under their categories
    subCategories
      .filter(sub => sub.MENUSUBCATEGORY_STATUS === 1)
      .forEach(subCategory => {
        const categoryId = subCategory.MENUCATEGORY_ID;
        
        // Find which type group this category belongs to
        const category = categories.find(cat => cat.MENUCATEGORY_ID === categoryId);
        if (category) {
          const type = category.MENUCATEGORY_TYPE || 'other';
          const typeKey = type === 'be' ? 'be' : type === 'fe' ? 'fe' : 'other';
          
          if (treeByType[typeKey] && treeByType[typeKey][categoryId]) {
            treeByType[typeKey][categoryId].subCategories[subCategory.MENUSUBCATEGORY_ID] = {
              subCategory,
              menus: []
            };
          }
        }
      });
    
    // Finally, add menus to their subcategories
    menus.forEach(menu => {
      const categoryId = menu.MENUCATEGORY_ID;
      const subCategoryId = menu.MENUSUBCATEGORY_ID;
      
      // Find which type group this menu belongs to
      const category = categories.find(cat => cat.MENUCATEGORY_ID === categoryId);
      if (category) {
        const type = category.MENUCATEGORY_TYPE || 'other';
        const typeKey = type === 'be' ? 'be' : type === 'fe' ? 'fe' : 'other';
        
        if (treeByType[typeKey]?.[categoryId]?.subCategories[subCategoryId]) {
          treeByType[typeKey][categoryId].subCategories[subCategoryId].menus.push(menu);
        }
      }
    });
    
    return treeByType;
  }, [menus, categories, subCategories]);

  const handleCreate = (subCategoryId = null) => {
    setEditingMenu(null);
    const subCategory = subCategoryId ? subCategories.find(sub => sub.MENUSUBCATEGORY_ID === subCategoryId) : null;
    setFormData({
      MENU_NAME: '',
      MENU_STATUS: 1,
      MENU_URL: '',
      MENUSUBCATEGORY_ID: subCategoryId || '',
      MENU_DEV: '',
      selectedCategoryId: subCategory ? subCategory.MENUCATEGORY_ID : null,
    });
    onFormOpen();
  };

  const handleEdit = (menu) => {
    setEditingMenu(menu);
    setFormData({
      MENU_NAME: menu.MENU_NAME || '',
      MENU_STATUS: menu.MENU_STATUS !== undefined ? menu.MENU_STATUS : 1,
      MENU_URL: menu.MENU_URL || '',
      MENUSUBCATEGORY_ID: menu.MENUSUBCATEGORY_ID || '',
      MENU_DEV: menu.MENU_DEV || '',
      selectedCategoryId: menu.MENUCATEGORY_ID || null,
    });
    onFormOpen();
  };

  const handleSubmit = async () => {
    if (!formData.MENU_NAME || formData.MENU_NAME.trim() === '') {
      toast({
        title: 'Validasi gagal',
        description: 'Nama Menu harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.MENUSUBCATEGORY_ID) {
      toast({
        title: 'Validasi gagal',
        description: 'Sub Category harus dipilih',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const CREATED_BY = userData.nip || userData.username || null;
      const UPDATED_BY = userData.nip || userData.username || null;

      if (editingMenu) {
        // Update
        const response = await menuApi.post(`/update/${editingMenu.MENU_ID}`, {
          MENU_NAME: formData.MENU_NAME.trim(),
          MENU_STATUS: formData.MENU_STATUS,
          MENU_URL: formData.MENU_URL || null,
          MENUSUBCATEGORY_ID: parseInt(formData.MENUSUBCATEGORY_ID),
          MENU_DEV: formData.MENU_DEV || null,
          UPDATED_BY,
        });

        if (response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'Menu berhasil diupdate',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onFormClose();
          fetchMenus();
        }
      } else {
        // Create
        const response = await menuApi.post('/', {
          MENU_NAME: formData.MENU_NAME.trim(),
          MENU_STATUS: formData.MENU_STATUS,
          MENU_URL: formData.MENU_URL || null,
          MENUSUBCATEGORY_ID: parseInt(formData.MENUSUBCATEGORY_ID),
          MENU_DEV: formData.MENU_DEV || null,
          CREATED_BY,
        });

        if (response.data.status === 201) {
          toast({
            title: 'Berhasil',
            description: 'Menu berhasil dibuat',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onFormClose();
          fetchMenus();
        }
      }
    } catch (error) {
      console.error('Error saving menu:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteClick = (menu) => {
    setDeleteMenu(menu);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteMenu) return;

    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const DELETED_BY = userData.nip || userData.username || null;

      const response = await menuApi.post(`/delete/${deleteMenu.MENU_ID}`, {
        DELETED_BY,
      });

      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'Menu berhasil dihapus',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onDeleteClose();
        setDeleteMenu(null);
        fetchMenus();
      }
    } catch (error) {
      console.error('Error deleting menu:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSearch = () => {
    fetchMenus();
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('');
    setTimeout(() => {
      fetchMenus();
    }, 100);
  };

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleSubCategory = (subCategoryId) => {
    const newExpanded = new Set(expandedSubCategories);
    if (newExpanded.has(subCategoryId)) {
      newExpanded.delete(subCategoryId);
    } else {
      newExpanded.add(subCategoryId);
    }
    setExpandedSubCategories(newExpanded);
  };

  // Filter active subcategories for dropdown
  const activeSubCategories = useMemo(() => {
    return subCategories.filter(sub => sub.MENUSUBCATEGORY_STATUS === 1);
  }, [subCategories]);

  // Get subcategories by category for dropdown
  const getSubCategoriesByCategory = useCallback((categoryId) => {
    if (!categoryId) return [];
    return activeSubCategories.filter(sub => sub.MENUCATEGORY_ID === categoryId);
  }, [activeSubCategories]);

  return (
    <Box p={{ base: 4, md: 6, lg: 8 }}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" flexWrap="wrap" gap={4}>
          <Heading size="lg">Manage Menu</Heading>
          <HStack spacing={2}>
            <Button
              leftIcon={<FiRefreshCw />}
              onClick={() => {
                fetchCategories();
                fetchSubCategories();
                fetchMenus();
              }}
              isLoading={loading}
              size={{ base: 'sm', md: 'md' }}
            >
              Refresh
            </Button>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={() => handleCreate()}
              size={{ base: 'sm', md: 'md' }}
            >
              Tambah Menu
            </Button>
          </HStack>
        </HStack>

        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              {/* Search and Filter */}
              <HStack spacing={4} flexWrap="wrap">
                <InputGroup flex="1" minW="200px">
                  <Input
                    placeholder="Cari nama menu..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                  <InputRightElement>
                    <IconButton
                      icon={<FiSearch />}
                      size="sm"
                      variant="ghost"
                      onClick={handleSearch}
                      aria-label="Search"
                    />
                  </InputRightElement>
                </InputGroup>
                <Select
                  placeholder="Filter Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  width={{ base: '100%', md: '200px' }}
                >
                  <option value="1">ACTIVE</option>
                  <option value="0">NOT ACTIVE</option>
                </Select>
                <Button
                  onClick={handleSearch}
                  colorScheme="blue"
                  size={{ base: 'sm', md: 'md' }}
                >
                  Cari
                </Button>
                <Button
                  onClick={handleReset}
                  variant="outline"
                  size={{ base: 'sm', md: 'md' }}
                >
                  Reset
                </Button>
              </HStack>

              {/* Tree View */}
              {loading && menus.length === 0 ? (
                <Box textAlign="center" py={8}>
                  <Spinner size="xl" />
                </Box>
              ) : (
                <Box>
                  {Object.keys(treeData.be).length === 0 && Object.keys(treeData.fe).length === 0 && Object.keys(treeData.other).length === 0 ? (
                    <Box py={8} textAlign="center">
                      <Text color="gray.500">Tidak ada data menu</Text>
                    </Box>
                  ) : (
                    <VStack align="stretch" spacing={6}>
                      {/* Back End Section */}
                      {Object.keys(treeData.be).length > 0 && (
                        <Box>
                          <Box
                            mb={4}
                            p={3}
                            bg="blue.50"
                            borderLeft="4px"
                            borderColor="blue.500"
                            borderRadius="md"
                          >
                            <HStack spacing={2}>
                              <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                                BACK END
                              </Badge>
                              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                                Menu untuk Back End System
                              </Text>
                            </HStack>
                          </Box>
                          <VStack align="stretch" spacing={2}>
                            {Object.entries(treeData.be).map(([categoryId, categoryData]) => {
                              const isCategoryExpanded = expandedCategories.has(parseInt(categoryId));
                              return (
                                <Box key={categoryId} border="1px" borderColor="blue.200" borderRadius="md" p={2}>
                                  {/* Category Level */}
                                  <HStack
                                    spacing={2}
                                    p={2}
                                    cursor="pointer"
                                    onClick={() => toggleCategory(parseInt(categoryId))}
                                    _hover={{ bg: 'blue.50' }}
                                    borderRadius="md"
                                  >
                                    <IconButton
                                      icon={isCategoryExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                      size="xs"
                                      variant="ghost"
                                      aria-label="Toggle"
                                    />
                                    <Text fontWeight="bold" fontSize="md" flex={1}>
                                      {categoryData.category.MENUCATEGORY_NAME}
                                    </Text>
                                    <Badge colorScheme="blue" fontSize="xs">
                                      Category
                                    </Badge>
                                  </HStack>

                            {/* Sub Categories */}
                            <Collapse in={isCategoryExpanded} animateOpacity>
                              <Box pl={6} mt={2}>
                                {Object.entries(categoryData.subCategories).map(([subCategoryId, subCategoryData]) => {
                                  const isSubCategoryExpanded = expandedSubCategories.has(parseInt(subCategoryId));
                                  const hasMenus = subCategoryData.menus.length > 0;
                                  
                                  // Jika subcategory tidak punya menu atau MENUSUBCATEGORY_HASMENU = 0, tampilkan sebagai menu langsung
                                  const shouldShowAsMenu = !hasMenus || subCategoryData.subCategory.MENUSUBCATEGORY_HASMENU === 0;
                                  
                                  if (shouldShowAsMenu) {
                                    return (
                                      <Box key={subCategoryId} mb={2} borderLeft="2px" borderColor="purple.300" pl={3}>
                                        <Box
                                          p={2}
                                          borderLeft="2px"
                                          borderColor="purple.200"
                                          bg="purple.50"
                                          borderRadius="md"
                                        >
                                          <HStack spacing={2} justify="space-between">
                                            <HStack spacing={2} flex={1}>
                                              {subCategoryData.subCategory.MENUSUBCATEGORY_ICON && FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON] && (
                                                <Icon as={FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON]} fontSize="md" color="purple.500" />
                                              )}
                                              <VStack align="start" spacing={0} flex={1}>
                                                <HStack spacing={2}>
                                                  <Text fontWeight="medium" fontSize="sm">
                                                    {subCategoryData.subCategory.MENUSUBCATEGORY_NAME}
                                                  </Text>
                                                  <Badge
                                                    colorScheme={subCategoryData.subCategory.MENUSUBCATEGORY_STATUS === 1 ? 'green' : 'red'}
                                                    fontSize="xs"
                                                  >
                                                    {subCategoryData.subCategory.MENUSUBCATEGORY_STATUS === 1 ? 'ACTIVE' : 'NOT ACTIVE'}
                                                  </Badge>
                                                  <Badge colorScheme="purple" fontSize="xs" variant="outline">
                                                    Sub Category (Menu)
                                                  </Badge>
                                                </HStack>
                                                {subCategoryData.subCategory.MENUSUBCATEGORY_URL && (
                                                  <Text
                                                    fontSize="xs"
                                                    color="blue.500"
                                                    as="a"
                                                    href={
                                                      toSecureUrl(subCategoryData.subCategory.MENUSUBCATEGORY_URL)
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    isTruncated
                                                    maxW="300px"
                                                    mt={1}
                                                    _hover={{ textDecoration: 'underline' }}
                                                  >
                                                    {toSecureUrl(subCategoryData.subCategory.MENUSUBCATEGORY_URL)}
                                                  </Text>
                                                )}
                                              </VStack>
                                            </HStack>
                                            <HStack spacing={1}>
                                              {hasMenus && (
                                                <IconButton
                                                  icon={<FiPlus />}
                                                  size="xs"
                                                  colorScheme="blue"
                                                  variant="ghost"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCreate(parseInt(subCategoryId));
                                                  }}
                                                  aria-label="Add Menu"
                                                  title="Tambah Menu"
                                                />
                                              )}
                                            </HStack>
                                          </HStack>
                                        </Box>
                                      </Box>
                                    );
                                  }
                                  
                                  // Jika subcategory punya menu, tampilkan sebagai parent dengan collapse
                                  return (
                                    <Box key={subCategoryId} mb={2} borderLeft="2px" borderColor="gray.300" pl={3}>
                                      {/* Sub Category Level */}
                                      <HStack
                                        spacing={2}
                                        p={2}
                                        cursor="pointer"
                                        onClick={() => toggleSubCategory(parseInt(subCategoryId))}
                                        _hover={{ bg: 'gray.50' }}
                                        borderRadius="md"
                                      >
                                        <IconButton
                                          icon={isSubCategoryExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                          size="xs"
                                          variant="ghost"
                                          aria-label="Toggle"
                                        />
                                        {subCategoryData.subCategory.MENUSUBCATEGORY_ICON && FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON] && (
                                          <Icon as={FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON]} fontSize="md" color="purple.500" />
                                        )}
                                        <Text fontWeight="semibold" fontSize="sm" flex={1}>
                                          {subCategoryData.subCategory.MENUSUBCATEGORY_NAME}
                                        </Text>
                                        <Badge colorScheme="purple" fontSize="xs">
                                          Sub Category
                                        </Badge>
                                        <IconButton
                                          icon={<FiPlus />}
                                          size="xs"
                                          colorScheme="blue"
                                          variant="ghost"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCreate(parseInt(subCategoryId));
                                          }}
                                          aria-label="Add Menu"
                                          title="Tambah Menu"
                                        />
                                      </HStack>

                                      {/* Menus */}
                                      <Collapse in={isSubCategoryExpanded} animateOpacity>
                                        <Box pl={6} mt={2}>
                                          {subCategoryData.menus.map((menu) => (
                                            <Box
                                              key={menu.MENU_ID}
                                              p={2}
                                              mb={1}
                                              borderLeft="2px"
                                              borderColor="blue.200"
                                              bg="blue.50"
                                              borderRadius="md"
                                            >
                                              <HStack spacing={2} justify="space-between">
                                                <VStack align="start" spacing={0} flex={1}>
                                                  <HStack spacing={2} flexWrap="wrap">
                                                    <Text fontWeight="medium" fontSize="sm">
                                                      {menu.MENU_NAME}
                                                    </Text>
                                                    <Badge
                                                      colorScheme={menu.MENU_STATUS === 1 ? 'green' : 'red'}
                                                      fontSize="xs"
                                                    >
                                                      {menu.MENU_STATUS === 1 ? 'ACTIVE' : 'NOT ACTIVE'}
                                                    </Badge>
                                                    {menu.MENU_DEV && (
                                                      <Badge
                                                        colorScheme={menu.MENU_DEV === 'y' ? 'orange' : menu.MENU_DEV === 'n' ? 'green' : 'gray'}
                                                        fontSize="xs"
                                                      >
                                                        Dev: {menu.MENU_DEV === 'y' ? 'Iya' : 'Tidak'}
                                                      </Badge>
                                                    )}
                                                  </HStack>
                                                  {menu.MENU_URL && (
                                                    <Text
                                                      fontSize="xs"
                                                      color="blue.500"
                                                      as="a"
                                                      href={
                                                        toSecureUrl(menu.MENU_URL)
                                                      }
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      isTruncated
                                                      maxW="300px"
                                                      _hover={{ textDecoration: 'underline' }}
                                                    >
                                                      {toSecureUrl(menu.MENU_URL)}
                                                    </Text>
                                                  )}
                                                </VStack>
                                                <HStack spacing={1}>
                                                  <IconButton
                                                    icon={<FiEdit />}
                                                    size="xs"
                                                    colorScheme="blue"
                                                    variant="ghost"
                                                    onClick={() => handleEdit(menu)}
                                                    aria-label="Edit"
                                                  />
                                                  <IconButton
                                                    icon={<FiTrash2 />}
                                                    size="xs"
                                                    colorScheme="red"
                                                    variant="ghost"
                                                    onClick={() => handleDeleteClick(menu)}
                                                    aria-label="Delete"
                                                  />
                                                </HStack>
                                              </HStack>
                                            </Box>
                                          ))}
                                        </Box>
                                      </Collapse>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </VStack>
                  </Box>
                )}

                      {/* Front End Section */}
                      {Object.keys(treeData.fe).length > 0 && (
                        <Box>
                          <Box
                            mb={4}
                            p={3}
                            bg="purple.50"
                            borderLeft="4px"
                            borderColor="purple.500"
                            borderRadius="md"
                          >
                            <HStack spacing={2}>
                              <Badge colorScheme="purple" fontSize="md" px={3} py={1}>
                                FRONT END
                              </Badge>
                              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                                Menu untuk Front End System
                              </Text>
                            </HStack>
                          </Box>
                          <VStack align="stretch" spacing={2}>
                            {Object.entries(treeData.fe).map(([categoryId, categoryData]) => {
                              const isCategoryExpanded = expandedCategories.has(parseInt(categoryId));
                              return (
                                <Box key={categoryId} border="1px" borderColor="purple.200" borderRadius="md" p={2}>
                                  {/* Category Level */}
                                  <HStack
                                    spacing={2}
                                    p={2}
                                    cursor="pointer"
                                    onClick={() => toggleCategory(parseInt(categoryId))}
                                    _hover={{ bg: 'purple.50' }}
                                    borderRadius="md"
                                  >
                                    <IconButton
                                      icon={isCategoryExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                      size="xs"
                                      variant="ghost"
                                      aria-label="Toggle"
                                    />
                                    <Text fontWeight="bold" fontSize="md" flex={1}>
                                      {categoryData.category.MENUCATEGORY_NAME}
                                    </Text>
                                    <Badge colorScheme="purple" fontSize="xs">
                                      Category
                                    </Badge>
                                  </HStack>

                                  {/* Sub Categories */}
                                  <Collapse in={isCategoryExpanded} animateOpacity>
                                    <Box pl={6} mt={2}>
                                      {Object.entries(categoryData.subCategories).map(([subCategoryId, subCategoryData]) => {
                                        const isSubCategoryExpanded = expandedSubCategories.has(parseInt(subCategoryId));
                                        const hasMenus = subCategoryData.menus.length > 0;
                                        
                                        // Jika subcategory tidak punya menu atau MENUSUBCATEGORY_HASMENU = 0, tampilkan sebagai menu langsung
                                        const shouldShowAsMenu = !hasMenus || subCategoryData.subCategory.MENUSUBCATEGORY_HASMENU === 0;
                                        
                                        if (shouldShowAsMenu) {
                                          return (
                                            <Box key={subCategoryId} mb={2} borderLeft="2px" borderColor="purple.300" pl={3}>
                                              <Box
                                                p={2}
                                                borderLeft="2px"
                                                borderColor="purple.200"
                                                bg="purple.50"
                                                borderRadius="md"
                                              >
                                                <HStack spacing={2} justify="space-between">
                                                  <HStack spacing={2} flex={1}>
                                                    {subCategoryData.subCategory.MENUSUBCATEGORY_ICON && FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON] && (
                                                      <Icon as={FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON]} fontSize="md" color="purple.500" />
                                                    )}
                                                    <VStack align="start" spacing={0} flex={1}>
                                                      <HStack spacing={2}>
                                                        <Text fontWeight="medium" fontSize="sm">
                                                          {subCategoryData.subCategory.MENUSUBCATEGORY_NAME}
                                                        </Text>
                                                        <Badge
                                                          colorScheme={subCategoryData.subCategory.MENUSUBCATEGORY_STATUS === 1 ? 'green' : 'red'}
                                                          fontSize="xs"
                                                        >
                                                          {subCategoryData.subCategory.MENUSUBCATEGORY_STATUS === 1 ? 'ACTIVE' : 'NOT ACTIVE'}
                                                        </Badge>
                                                        <Badge colorScheme="purple" fontSize="xs" variant="outline">
                                                          Sub Category (Menu)
                                                        </Badge>
                                                      </HStack>
                                                      {subCategoryData.subCategory.MENUSUBCATEGORY_URL && (
                                                        <Text
                                                          fontSize="xs"
                                                          color="blue.500"
                                                          as="a"
                                                          href={
                                                            toSecureUrl(subCategoryData.subCategory.MENUSUBCATEGORY_URL)
                                                          }
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          isTruncated
                                                          maxW="300px"
                                                          mt={1}
                                                          _hover={{ textDecoration: 'underline' }}
                                                        >
                                                          {toSecureUrl(subCategoryData.subCategory.MENUSUBCATEGORY_URL)}
                                                        </Text>
                                                      )}
                                                    </VStack>
                                                  </HStack>
                                                  <HStack spacing={1}>
                                                    {hasMenus && (
                                                      <IconButton
                                                        icon={<FiPlus />}
                                                        size="xs"
                                                        colorScheme="blue"
                                                        variant="ghost"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleCreate(parseInt(subCategoryId));
                                                        }}
                                                        aria-label="Add Menu"
                                                        title="Tambah Menu"
                                                      />
                                                    )}
                                                  </HStack>
                                                </HStack>
                                              </Box>
                                            </Box>
                                          );
                                        }
                                        
                                        // Jika subcategory punya menu, tampilkan sebagai parent dengan collapse
                                        return (
                                          <Box key={subCategoryId} mb={2} borderLeft="2px" borderColor="gray.300" pl={3}>
                                            {/* Sub Category Level */}
                                            <HStack
                                              spacing={2}
                                              p={2}
                                              cursor="pointer"
                                              onClick={() => toggleSubCategory(parseInt(subCategoryId))}
                                              _hover={{ bg: 'gray.50' }}
                                              borderRadius="md"
                                            >
                                              <IconButton
                                                icon={isSubCategoryExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                                size="xs"
                                                variant="ghost"
                                                aria-label="Toggle"
                                              />
                                              {subCategoryData.subCategory.MENUSUBCATEGORY_ICON && FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON] && (
                                                <Icon as={FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON]} fontSize="md" color="purple.500" />
                                              )}
                                              <Text fontWeight="semibold" fontSize="sm" flex={1}>
                                                {subCategoryData.subCategory.MENUSUBCATEGORY_NAME}
                                              </Text>
                                              <Badge colorScheme="purple" fontSize="xs">
                                                Sub Category
                                              </Badge>
                                              <IconButton
                                                icon={<FiPlus />}
                                                size="xs"
                                                colorScheme="blue"
                                                variant="ghost"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleCreate(parseInt(subCategoryId));
                                                }}
                                                aria-label="Add Menu"
                                                title="Tambah Menu"
                                              />
                                            </HStack>

                                            {/* Menus */}
                                            <Collapse in={isSubCategoryExpanded} animateOpacity>
                                              <Box pl={6} mt={2}>
                                                {subCategoryData.menus.map((menu) => (
                                                  <Box
                                                    key={menu.MENU_ID}
                                                    p={2}
                                                    mb={1}
                                                    borderLeft="2px"
                                                    borderColor="blue.200"
                                                    bg="blue.50"
                                                    borderRadius="md"
                                                  >
                                                    <HStack spacing={2} justify="space-between">
                                                      <VStack align="start" spacing={0} flex={1}>
                                                        <HStack spacing={2} flexWrap="wrap">
                                                          <Text fontWeight="medium" fontSize="sm">
                                                            {menu.MENU_NAME}
                                                          </Text>
                                                          <Badge
                                                            colorScheme={menu.MENU_STATUS === 1 ? 'green' : 'red'}
                                                            fontSize="xs"
                                                          >
                                                            {menu.MENU_STATUS === 1 ? 'ACTIVE' : 'NOT ACTIVE'}
                                                          </Badge>
                                                          {menu.MENU_DEV && (
                                                            <Badge
                                                              colorScheme={menu.MENU_DEV === 'y' ? 'orange' : menu.MENU_DEV === 'n' ? 'green' : 'gray'}
                                                              fontSize="xs"
                                                            >
                                                              Dev: {menu.MENU_DEV === 'y' ? 'Iya' : 'Tidak'}
                                                            </Badge>
                                                          )}
                                                        </HStack>
                                                        {menu.MENU_URL && (
                                                          <Text
                                                            fontSize="xs"
                                                            color="blue.500"
                                                            as="a"
                                                            href={
                                                              toSecureUrl(menu.MENU_URL)
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            isTruncated
                                                            maxW="300px"
                                                            _hover={{ textDecoration: 'underline' }}
                                                          >
                                                            {toSecureUrl(menu.MENU_URL)}
                                                          </Text>
                                                        )}
                                                      </VStack>
                                                      <HStack spacing={1}>
                                                        <IconButton
                                                          icon={<FiEdit />}
                                                          size="xs"
                                                          colorScheme="blue"
                                                          variant="ghost"
                                                          onClick={() => handleEdit(menu)}
                                                          aria-label="Edit"
                                                        />
                                                        <IconButton
                                                          icon={<FiTrash2 />}
                                                          size="xs"
                                                          colorScheme="red"
                                                          variant="ghost"
                                                          onClick={() => handleDeleteClick(menu)}
                                                          aria-label="Delete"
                                                        />
                                                      </HStack>
                                                    </HStack>
                                                  </Box>
                                                ))}
                                              </Box>
                                            </Collapse>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </VStack>
                        </Box>
                      )}

                      {/* Other Section (No Type) */}
                      {Object.keys(treeData.other).length > 0 && (
                        <Box>
                          <Box
                            mb={4}
                            p={3}
                            bg="gray.50"
                            borderLeft="4px"
                            borderColor="gray.400"
                            borderRadius="md"
                          >
                            <HStack spacing={2}>
                              <Badge colorScheme="gray" fontSize="md" px={3} py={1}>
                                OTHER
                              </Badge>
                              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                                Menu tanpa type (belum dikategorikan)
                              </Text>
                            </HStack>
                          </Box>
                          <VStack align="stretch" spacing={2}>
                            {Object.entries(treeData.other).map(([categoryId, categoryData]) => {
                              const isCategoryExpanded = expandedCategories.has(parseInt(categoryId));
                              return (
                                <Box key={categoryId} border="1px" borderColor="gray.200" borderRadius="md" p={2}>
                                  {/* Category Level */}
                                  <HStack
                                    spacing={2}
                                    p={2}
                                    cursor="pointer"
                                    onClick={() => toggleCategory(parseInt(categoryId))}
                                    _hover={{ bg: 'gray.50' }}
                                    borderRadius="md"
                                  >
                                    <IconButton
                                      icon={isCategoryExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                      size="xs"
                                      variant="ghost"
                                      aria-label="Toggle"
                                    />
                                    <Text fontWeight="bold" fontSize="md" flex={1}>
                                      {categoryData.category.MENUCATEGORY_NAME}
                                    </Text>
                                    <Badge colorScheme="gray" fontSize="xs">
                                      Category
                                    </Badge>
                                  </HStack>

                                  {/* Sub Categories */}
                                  <Collapse in={isCategoryExpanded} animateOpacity>
                                    <Box pl={6} mt={2}>
                                      {Object.entries(categoryData.subCategories).map(([subCategoryId, subCategoryData]) => {
                                        const isSubCategoryExpanded = expandedSubCategories.has(parseInt(subCategoryId));
                                        const hasMenus = subCategoryData.menus.length > 0;
                                        
                                        // Jika subcategory tidak punya menu atau MENUSUBCATEGORY_HASMENU = 0, tampilkan sebagai menu langsung
                                        const shouldShowAsMenu = !hasMenus || subCategoryData.subCategory.MENUSUBCATEGORY_HASMENU === 0;
                                        
                                        if (shouldShowAsMenu) {
                                          return (
                                            <Box key={subCategoryId} mb={2} borderLeft="2px" borderColor="purple.300" pl={3}>
                                              <Box
                                                p={2}
                                                borderLeft="2px"
                                                borderColor="purple.200"
                                                bg="purple.50"
                                                borderRadius="md"
                                              >
                                                <HStack spacing={2} justify="space-between">
                                                  <HStack spacing={2} flex={1}>
                                                    {subCategoryData.subCategory.MENUSUBCATEGORY_ICON && FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON] && (
                                                      <Icon as={FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON]} fontSize="md" color="purple.500" />
                                                    )}
                                                    <VStack align="start" spacing={0} flex={1}>
                                                      <HStack spacing={2}>
                                                        <Text fontWeight="medium" fontSize="sm">
                                                          {subCategoryData.subCategory.MENUSUBCATEGORY_NAME}
                                                        </Text>
                                                        <Badge
                                                          colorScheme={subCategoryData.subCategory.MENUSUBCATEGORY_STATUS === 1 ? 'green' : 'red'}
                                                          fontSize="xs"
                                                        >
                                                          {subCategoryData.subCategory.MENUSUBCATEGORY_STATUS === 1 ? 'ACTIVE' : 'NOT ACTIVE'}
                                                        </Badge>
                                                        <Badge colorScheme="purple" fontSize="xs" variant="outline">
                                                          Sub Category (Menu)
                                                        </Badge>
                                                      </HStack>
                                                      {subCategoryData.subCategory.MENUSUBCATEGORY_URL && (
                                                        <Text
                                                          fontSize="xs"
                                                          color="blue.500"
                                                          as="a"
                                                          href={
                                                            toSecureUrl(subCategoryData.subCategory.MENUSUBCATEGORY_URL)
                                                          }
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          isTruncated
                                                          maxW="300px"
                                                          mt={1}
                                                          _hover={{ textDecoration: 'underline' }}
                                                        >
                                                          {toSecureUrl(subCategoryData.subCategory.MENUSUBCATEGORY_URL)}
                                                        </Text>
                                                      )}
                                                    </VStack>
                                                  </HStack>
                                                  <HStack spacing={1}>
                                                    {hasMenus && (
                                                      <IconButton
                                                        icon={<FiPlus />}
                                                        size="xs"
                                                        colorScheme="blue"
                                                        variant="ghost"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleCreate(parseInt(subCategoryId));
                                                        }}
                                                        aria-label="Add Menu"
                                                        title="Tambah Menu"
                                                      />
                                                    )}
                                                  </HStack>
                                                </HStack>
                                              </Box>
                                            </Box>
                                          );
                                        }
                                        
                                        // Jika subcategory punya menu, tampilkan sebagai parent dengan collapse
                                        return (
                                          <Box key={subCategoryId} mb={2} borderLeft="2px" borderColor="gray.300" pl={3}>
                                            {/* Sub Category Level */}
                                            <HStack
                                              spacing={2}
                                              p={2}
                                              cursor="pointer"
                                              onClick={() => toggleSubCategory(parseInt(subCategoryId))}
                                              _hover={{ bg: 'gray.50' }}
                                              borderRadius="md"
                                            >
                                              <IconButton
                                                icon={isSubCategoryExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                                size="xs"
                                                variant="ghost"
                                                aria-label="Toggle"
                                              />
                                              {subCategoryData.subCategory.MENUSUBCATEGORY_ICON && FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON] && (
                                                <Icon as={FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON]} fontSize="md" color="purple.500" />
                                              )}
                                              <Text fontWeight="semibold" fontSize="sm" flex={1}>
                                                {subCategoryData.subCategory.MENUSUBCATEGORY_NAME}
                                              </Text>
                                              <Badge colorScheme="purple" fontSize="xs">
                                                Sub Category
                                              </Badge>
                                              <IconButton
                                                icon={<FiPlus />}
                                                size="xs"
                                                colorScheme="blue"
                                                variant="ghost"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleCreate(parseInt(subCategoryId));
                                                }}
                                                aria-label="Add Menu"
                                                title="Tambah Menu"
                                              />
                                            </HStack>

                                            {/* Menus */}
                                            <Collapse in={isSubCategoryExpanded} animateOpacity>
                                              <Box pl={6} mt={2}>
                                                {subCategoryData.menus.map((menu) => (
                                                  <Box
                                                    key={menu.MENU_ID}
                                                    p={2}
                                                    mb={1}
                                                    borderLeft="2px"
                                                    borderColor="blue.200"
                                                    bg="blue.50"
                                                    borderRadius="md"
                                                  >
                                                    <HStack spacing={2} justify="space-between">
                                                      <VStack align="start" spacing={0} flex={1}>
                                                        <HStack spacing={2} flexWrap="wrap">
                                                          <Text fontWeight="medium" fontSize="sm">
                                                            {menu.MENU_NAME}
                                                          </Text>
                                                          <Badge
                                                            colorScheme={menu.MENU_STATUS === 1 ? 'green' : 'red'}
                                                            fontSize="xs"
                                                          >
                                                            {menu.MENU_STATUS === 1 ? 'ACTIVE' : 'NOT ACTIVE'}
                                                          </Badge>
                                                          {menu.MENU_DEV && (
                                                            <Badge
                                                              colorScheme={menu.MENU_DEV === 'y' ? 'orange' : menu.MENU_DEV === 'n' ? 'green' : 'gray'}
                                                              fontSize="xs"
                                                            >
                                                              Dev: {menu.MENU_DEV === 'y' ? 'Iya' : 'Tidak'}
                                                            </Badge>
                                                          )}
                                                        </HStack>
                                                        {menu.MENU_URL && (
                                                          <Text
                                                            fontSize="xs"
                                                            color="blue.500"
                                                            as="a"
                                                            href={
                                                              toSecureUrl(menu.MENU_URL)
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            isTruncated
                                                            maxW="300px"
                                                            _hover={{ textDecoration: 'underline' }}
                                                          >
                                                            {toSecureUrl(menu.MENU_URL)}
                                                          </Text>
                                                        )}
                                                      </VStack>
                                                      <HStack spacing={1}>
                                                        <IconButton
                                                          icon={<FiEdit />}
                                                          size="xs"
                                                          colorScheme="blue"
                                                          variant="ghost"
                                                          onClick={() => handleEdit(menu)}
                                                          aria-label="Edit"
                                                        />
                                                        <IconButton
                                                          icon={<FiTrash2 />}
                                                          size="xs"
                                                          colorScheme="red"
                                                          variant="ghost"
                                                          onClick={() => handleDeleteClick(menu)}
                                                          aria-label="Delete"
                                                        />
                                                      </HStack>
                                                    </HStack>
                                                  </Box>
                                                ))}
                                              </Box>
                                            </Collapse>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </VStack>
                        </Box>
                      )}
                    </VStack>
                  )}
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Create/Edit Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingMenu ? 'Edit Menu' : 'Tambah Menu'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select
                  placeholder="Pilih Category"
                  value={formData.selectedCategoryId || ''}
                  onChange={(e) => {
                    const categoryId = parseInt(e.target.value);
                    setFormData({ 
                      ...formData, 
                      selectedCategoryId: categoryId,
                      MENUSUBCATEGORY_ID: '' // Reset subcategory when category changes
                    });
                  }}
                  isDisabled={loadingCategories}
                >
                  {categories.filter(cat => cat.MENUCATEGORY_STATUS === 1).map((cat) => (
                    <option key={cat.MENUCATEGORY_ID} value={cat.MENUCATEGORY_ID}>
                      {cat.MENUCATEGORY_NAME}
                    </option>
                  ))}
                </Select>
                {loadingCategories && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Memuat categories...
                  </Text>
                )}
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Sub Category</FormLabel>
                <Select
                  placeholder="Pilih Sub Category"
                  value={formData.MENUSUBCATEGORY_ID}
                  onChange={(e) =>
                    setFormData({ ...formData, MENUSUBCATEGORY_ID: e.target.value })
                  }
                  isDisabled={!formData.selectedCategoryId || loadingCategories}
                >
                  {formData.selectedCategoryId && 
                    getSubCategoriesByCategory(formData.selectedCategoryId).map((sub) => (
                      <option key={sub.MENUSUBCATEGORY_ID} value={sub.MENUSUBCATEGORY_ID}>
                        {sub.MENUSUBCATEGORY_NAME}
                      </option>
                    ))
                  }
                </Select>
                {!formData.selectedCategoryId && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Pilih Category terlebih dahulu
                  </Text>
                )}
                {formData.selectedCategoryId && getSubCategoriesByCategory(formData.selectedCategoryId).length === 0 && (
                  <Text fontSize="xs" color="orange.500" mt={1}>
                    Tidak ada subcategory aktif untuk category ini
                  </Text>
                )}
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Nama Menu</FormLabel>
                <Input
                  placeholder="Masukkan nama menu"
                  value={formData.MENU_NAME}
                  onChange={(e) =>
                    setFormData({ ...formData, MENU_NAME: e.target.value })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>URL</FormLabel>
                <Input
                  placeholder="Masukkan URL (opsional)"
                  value={formData.MENU_URL}
                  onChange={(e) =>
                    setFormData({ ...formData, MENU_URL: e.target.value })
                  }
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Contoh: /admin/dashboard atau https://example.com
                </Text>
                {formData.MENU_URL && (
                  <Box mt={2} p={2} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                      Preview URL:
                    </Text>
                    <Text
                      fontSize="sm"
                      color="blue.600"
                      as="a"
                      href={
                        toSecureUrl(formData.MENU_URL)
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      wordBreak="break-all"
                    >
                      {toSecureUrl(formData.MENU_URL)}
                    </Text>
                  </Box>
                )}
              </FormControl>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={formData.MENU_STATUS}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      MENU_STATUS: parseInt(e.target.value),
                    })
                  }
                >
                  <option value={1}>ACTIVE</option>
                  <option value={0}>NOT ACTIVE</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Development</FormLabel>
                <Select
                  placeholder="Pilih Status Development"
                  value={formData.MENU_DEV}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      MENU_DEV: e.target.value,
                    })
                  }
                >
                  <option value="">- Pilih Status -</option>
                  <option value="y">Iya (Sedang Development)</option>
                  <option value="n">Tidak (Tidak Development)</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>
              Batal
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {editingMenu ? 'Update' : 'Simpan'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus Menu
            </AlertDialogHeader>
            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus menu "{deleteMenu?.MENU_NAME}"?
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Batal
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ManageMenu;

